import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { useUpdateApiKeyMutation } from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { CommonModal } from "../../../Common/CommonModal";
import Modal from "../../../Common/Modal";
import { Text } from "../../../Inputs/react-hook-form";
import { AccountAPIKey } from "./__queries__/apiKeyQueries";
import { validateApiKeyName } from "./validation";

type FormData = {
  name: string;
};

export const useEditAPIKeyModal = ({
  apiKey,
  existingNames,
  refetch,
}: {
  apiKey: AccountAPIKey;
  existingNames: Array<string>;
  refetch: () => void;
}) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <EditAPIKeyModal
          apiKey={apiKey}
          existingNames={existingNames}
          onClose={hideModal}
          refetch={refetch}
        />
      </Modal>
    ),
    [apiKey, existingNames, refetch],
  );
  return [showModal, hideModal] as const;
};

export const EditAPIKeyModal = ({
  apiKey,
  existingNames,
  onClose,
  refetch,
}: {
  apiKey: AccountAPIKey;
  existingNames: Array<string>;
  onClose: () => void;
  refetch: () => void;
}) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm<FormData>({
    defaultValues: { name: apiKey.name },
    mode: "onChange",
  });

  const [updateAPIKey, { loading }] = useUpdateApiKeyMutation();

  const onSubmit = async (formData: FormData) => {
    await updateAPIKey({
      variables: { data: { id: apiKey.id, name: formData.name.trim() } },
      onCompleted: () => {
        refetch();
        addSuccessToast("API key renamed successfully");
        onClose();
      },
      onError: error => {
        addErrorToast(error.message);
      },
    });
  };

  return (
    <CommonModal
      title="Rename API key"
      onCancel={onClose}
      onSubmit={handleSubmit(formData => void onSubmit(formData))}
      loading={loading}
      disabled={!isValid || !isDirty}
      content={
        <Text
          label="Name"
          error={errors.name?.message}
          {...register("name", validateApiKeyName(existingNames, apiKey.name))}
          required
        />
      }
    />
  );
};
